import { useMemo } from 'react'

import { useCategoriesData } from './useCategoriesData'
import { useCmsBlocks } from './useCmsBlocks'

import type { CategoriesState } from '../../types'

/**
 * Composition hook for categories and products state
 * Combines categories data and CMS blocks
 *
 * IMPORTANT: useMemo is REQUIRED here because this hook's return value is used
 * directly as Provider context value in CategoryAndProductsProvider. Without useMemo,
 * every render creates a new object reference, causing all context consumers to re-render.
 */
export function useCategoriesState(): CategoriesState {
  const categoriesData = useCategoriesData()
  const cmsBlocks = useCmsBlocks()

  return useMemo(
    () => ({
      categoriesData: categoriesData.categoriesData,
      firstLevelCategories: categoriesData.firstLevelCategories,
      blockData1: cmsBlocks.blockData1,
      blockData2: cmsBlocks.blockData2,
      loading: categoriesData.loading || cmsBlocks.loading,
      error: categoriesData.error || cmsBlocks.error,
    }),
    [
      categoriesData.categoriesData,
      categoriesData.firstLevelCategories,
      categoriesData.loading,
      categoriesData.error,
      cmsBlocks.blockData1,
      cmsBlocks.blockData2,
      cmsBlocks.loading,
      cmsBlocks.error,
    ]
  )
}
